/**
 * Dependency-free inline-SVG charts for Insights Studio (matches the dashboards
 * convention of hand-rolled charts — no chart library). All charts are display-only
 * except where an onSelect handler makes rows/slices clickable for drill-down.
 */

/** Series palette (readable in light + dark mode). */
export const CHART_COLORS = [
  '#4f46e5', // indigo
  '#0891b2', // cyan
  '#d97706', // amber
  '#059669', // emerald
  '#db2777', // pink
  '#7c3aed', // violet
  '#dc2626', // red
  '#2563eb', // blue
  '#65a30d', // lime
  '#c026d3', // fuchsia
  '#ea580c', // orange
  '#0d9488', // teal
  '#94a3b8', // slate (used for "Other")
] as const;

export function seriesColor(index: number, label?: string): string {
  if (label === 'Other') return '#94a3b8';
  return CHART_COLORS[index % (CHART_COLORS.length - 1)]!;
}

// ---------------------------------------------------------------------------
// Horizontal bar (rankings)
// ---------------------------------------------------------------------------

export interface BarRow {
  label: string;
  value: number;
  clickable?: boolean;
}

interface HBarChartProps {
  rows: BarRow[];
  format: (value: number) => string;
  onSelect?: (index: number) => void;
}

export function HBarChart({ rows, format, onSelect }: HBarChartProps): JSX.Element {
  const max = Math.max(...rows.map((r) => Math.abs(r.value)), 1);
  return (
    <div className="space-y-1.5">
      {rows.map((row, i) => {
        const pct = Math.max((Math.abs(row.value) / max) * 100, 0.5);
        const clickable = Boolean(onSelect && row.clickable !== false);
        const inner = (
          <>
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="truncate text-slate-700" title={row.label}>
                {row.label}
              </span>
              <span className="shrink-0 font-medium tabular-nums text-slate-900">
                {format(row.value)}
              </span>
            </div>
            <div className="mt-0.5 h-2.5 w-full rounded bg-slate-100">
              <div
                className="h-2.5 rounded"
                style={{ width: `${pct}%`, backgroundColor: seriesColor(i, row.label) }}
              />
            </div>
          </>
        );
        return clickable ? (
          <button
            key={i}
            type="button"
            onClick={() => onSelect!(i)}
            className="block w-full rounded px-1 py-0.5 text-left hover:bg-slate-50"
            title="View the entries behind this group"
          >
            {inner}
          </button>
        ) : (
          <div key={i} className="px-1 py-0.5">
            {inner}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stacked / grouped columns over categories (trends & composition)
// ---------------------------------------------------------------------------

export interface StackedSeries {
  label: string;
  /** One value per category (aligned with `categories`). */
  values: number[];
}

interface StackedBarChartProps {
  categories: string[];
  series: StackedSeries[];
  format: (value: number) => string;
  onSelect?: (categoryIndex: number) => void;
}

const STACK_W = 720;
const STACK_H = 200;
const STACK_PAD = 4;

export function StackedBarChart({
  categories,
  series,
  format,
  onSelect,
}: StackedBarChartProps): JSX.Element {
  const totals = categories.map((_, ci) => series.reduce((sum, s) => sum + (s.values[ci] ?? 0), 0));
  const max = Math.max(...totals, 1);
  const bw = Math.max((STACK_W - STACK_PAD * categories.length) / Math.max(categories.length, 1), 4);

  return (
    <div>
      <svg viewBox={`0 0 ${STACK_W} ${STACK_H + 18}`} className="w-full" role="img">
        {categories.map((cat, ci) => {
          let y = STACK_H;
          const x = ci * (bw + STACK_PAD);
          return (
            <g
              key={ci}
              onClick={onSelect ? () => onSelect(ci) : undefined}
              className={onSelect ? 'cursor-pointer' : undefined}
            >
              <title>{`${cat}: ${format(totals[ci] ?? 0)}`}</title>
              {series.map((s, si) => {
                const value = s.values[ci] ?? 0;
                const h = (Math.max(value, 0) / max) * STACK_H;
                y -= h;
                return h > 0 ? (
                  <rect
                    key={si}
                    x={x}
                    y={y}
                    width={bw}
                    height={h}
                    fill={seriesColor(si, s.label)}
                    rx={1}
                  />
                ) : null;
              })}
              {categories.length <= 24 ? (
                <text
                  x={x + bw / 2}
                  y={STACK_H + 13}
                  textAnchor="middle"
                  className="fill-slate-500"
                  fontSize={9}
                >
                  {shortCategory(cat)}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {series.length > 1 ? <ChartLegend labels={series.map((s) => s.label)} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Line chart (trends)
// ---------------------------------------------------------------------------

interface LineChartProps {
  categories: string[];
  series: StackedSeries[];
  format: (value: number) => string;
}

export function LineChart({ categories, series, format }: LineChartProps): JSX.Element {
  const max = Math.max(...series.flatMap((s) => s.values), 1);
  const step = categories.length > 1 ? STACK_W / (categories.length - 1) : 0;
  const pointY = (v: number): number => STACK_H - (Math.max(v, 0) / max) * (STACK_H - 8);

  return (
    <div>
      <svg viewBox={`0 0 ${STACK_W} ${STACK_H + 18}`} className="w-full" role="img">
        {series.map((s, si) => {
          const path = s.values
            .map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${pointY(v).toFixed(1)}`)
            .join(' ');
          return (
            <g key={si}>
              <path d={path} fill="none" stroke={seriesColor(si, s.label)} strokeWidth={2} />
              {s.values.map((v, i) => (
                <circle
                  key={i}
                  cx={i * step}
                  cy={pointY(v)}
                  r={2.5}
                  fill={seriesColor(si, s.label)}
                >
                  <title>{`${categories[i]}: ${format(v)}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
        {categories.map((cat, i) =>
          categories.length <= 18 || i % Math.ceil(categories.length / 12) === 0 ? (
            <text
              key={i}
              x={i * step}
              y={STACK_H + 13}
              textAnchor="middle"
              className="fill-slate-500"
              fontSize={9}
            >
              {shortCategory(cat)}
            </text>
          ) : null,
        )}
      </svg>
      {series.length > 1 ? <ChartLegend labels={series.map((s) => s.label)} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Donut (composition)
// ---------------------------------------------------------------------------

export interface DonutSlice {
  label: string;
  value: number;
}

interface DonutChartProps {
  slices: DonutSlice[];
  format: (value: number) => string;
  onSelect?: (index: number) => void;
}

export function DonutChart({ slices, format, onSelect }: DonutChartProps): JSX.Element {
  const total = slices.reduce((sum, s) => sum + Math.max(s.value, 0), 0) || 1;
  const R = 70;
  const STROKE = 30;
  const C = 2 * Math.PI * R;
  let offset = 0;

  return (
    <div className="flex flex-wrap items-center gap-5">
      <svg viewBox="0 0 180 180" className="h-44 w-44 shrink-0" role="img">
        {slices.map((slice, i) => {
          const frac = Math.max(slice.value, 0) / total;
          const dash = frac * C;
          const el = (
            <circle
              key={i}
              cx={90}
              cy={90}
              r={R}
              fill="none"
              stroke={seriesColor(i, slice.label)}
              strokeWidth={STROKE}
              strokeDasharray={`${dash} ${C - dash}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 90 90)"
              className={onSelect ? 'cursor-pointer' : undefined}
              onClick={onSelect ? () => onSelect(i) : undefined}
            >
              <title>{`${slice.label}: ${format(slice.value)} (${(frac * 100).toFixed(1)}%)`}</title>
            </circle>
          );
          offset += dash;
          return el;
        })}
      </svg>
      <ul className="min-w-0 flex-1 space-y-1 text-xs">
        {slices.map((slice, i) => (
          <li key={i} className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: seriesColor(i, slice.label) }}
            />
            <span className="truncate text-slate-700" title={slice.label}>
              {slice.label}
            </span>
            <span className="ml-auto shrink-0 font-medium tabular-nums text-slate-900">
              {format(slice.value)}
            </span>
            <span className="w-12 shrink-0 text-right tabular-nums text-slate-400">
              {((Math.max(slice.value, 0) / total) * 100).toFixed(1)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// GitHub-style hours heatmap calendar
// ---------------------------------------------------------------------------

interface HeatmapCalendarProps {
  /** Hours per ISO day (missing days render empty). */
  days: Array<{ date: string; hours: number }>;
  from: string;
  to: string;
}

const CELL = 12;
const CELL_GAP = 3;
const MONTH_LABEL_H = 14;

export function HeatmapCalendar({ days, from, to }: HeatmapCalendarProps): JSX.Element {
  const byDate = new Map(days.map((d) => [d.date, d.hours]));
  const start = mondayBefore(from);
  const end = new Date(`${to}T00:00:00.000Z`);
  const weeks: string[][] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const week: string[] = [];
    for (let i = 0; i < 7; i += 1) {
      week.push(cursor.toISOString().slice(0, 10));
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    weeks.push(week);
  }
  const width = weeks.length * (CELL + CELL_GAP);
  const height = MONTH_LABEL_H + 7 * (CELL + CELL_GAP);
  const maxHours = Math.max(...days.map((d) => d.hours), 8);

  return (
    <div className="overflow-x-auto">
      <svg width={width} height={height} role="img" aria-label="Hours heatmap">
        {weeks.map((week, wi) => {
          const monthLabel = monthBoundaryLabel(week[0]!, weeks[wi - 1]?.[0]);
          return (
            <g key={wi} transform={`translate(${wi * (CELL + CELL_GAP)},0)`}>
              {monthLabel ? (
                <text x={0} y={10} fontSize={9} className="fill-slate-500">
                  {monthLabel}
                </text>
              ) : null}
              {week.map((date, di) => {
                if (date < from || date > to) return null;
                const hours = byDate.get(date) ?? 0;
                return (
                  <rect
                    key={di}
                    x={0}
                    y={MONTH_LABEL_H + di * (CELL + CELL_GAP)}
                    width={CELL}
                    height={CELL}
                    rx={2}
                    fill={heatColor(hours, maxHours)}
                  >
                    <title>{`${date}: ${hours.toFixed(1)}h`}</title>
                  </rect>
                );
              })}
            </g>
          );
        })}
      </svg>
      <div className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-500">
        <span>Less</span>
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <span
            key={f}
            className="h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: heatColor(f * maxHours, maxHours) }}
          />
        ))}
        <span>More</span>
      </div>
    </div>
  );
}

function heatColor(hours: number, max: number): string {
  if (hours <= 0) return 'var(--color-slate-100, #f1f5f9)';
  const t = Math.min(hours / max, 1);
  if (t < 0.25) return '#c7d2fe';
  if (t < 0.5) return '#818cf8';
  if (t < 0.75) return '#6366f1';
  return '#4338ca';
}

function mondayBefore(iso: string): Date {
  const d = new Date(`${iso}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d;
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function monthBoundaryLabel(weekStart: string, prevWeekStart: string | undefined): string | null {
  const month = weekStart.slice(5, 7);
  if (prevWeekStart && prevWeekStart.slice(5, 7) === month) return null;
  return MONTH_SHORT[Number(month) - 1] ?? null;
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function ChartLegend({ labels }: { labels: string[] }): JSX.Element {
  return (
    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
      {labels.map((label, i) => (
        <span key={i} className="inline-flex items-center gap-1.5">
          <span
            className="h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: seriesColor(i, label) }}
          />
          <span className="max-w-40 truncate" title={label}>
            {label}
          </span>
        </span>
      ))}
    </div>
  );
}

/** Shortens long category labels for axis ticks (month keys pass through). */
function shortCategory(label: string): string {
  return label.length > 10 ? `${label.slice(0, 9)}…` : label;
}
